/**
 * Folder-tree refActions: consumer callbacks (render, sort, rename, creatable) wired with
 * shared defaults. Used by `useFolderTreeProvider` (immutable context adapters) and
 * `useFolderTreeProxyRefActions` (stable ref-forwarding object).
 *
 * Data state → `folderTree.tsx` · ephemeral UI → `treeInteraction.tsx` ·
 * rename/creatable/sort handlers → `folderTreeRuntime.tsx`.
 */ function createFolderTreeRefActions(getActions) {
    const resolve = ()=>getActions();
    return {
        renderItem: (...args)=>resolve().renderItem?.(...args),
        renderIcon: (...args)=>resolve().renderIcon?.(...args),
        onAddClose: (...args)=>resolve().onAddClose?.(...args),
        onSelect: (...args)=>resolve().onSelect?.(...args),
        onCloseMultiToolbar: (...args)=>resolve().onCloseMultiToolbar?.(...args),
        onSearchResult: (...args)=>resolve().onSearchResult?.(...args),
        onNodeExpand: (...args)=>resolve().onNodeExpand?.(...args),
        onCheck: (...args)=>resolve().onCheck?.(...args),
        onSort: (...args)=>resolve().onSort?.(...args),
        onSortPersistStart: (...args)=>resolve().onSortPersistStart?.(...args),
        onSortPersistSuccess: (...args)=>resolve().onSortPersistSuccess?.(...args),
        onSortPersistError: (...args)=>resolve().onSortPersistError?.(...args),
        onSortStart: (...args)=>resolve().onSortStart?.(...args),
        onSortEnd: (...args)=>resolve().onSortEnd?.(...args),
        onSortMove: (...args)=>resolve().onSortMove?.(...args),
        onResize: (...args)=>resolve().onResize?.(...args),
        onResizeFinished: (...args)=>resolve().onResizeFinished?.(...args),
        onResizeOpposite: (...args)=>resolve().onResizeOpposite?.(...args),
        onTreeStructureChanged: (...args)=>resolve().onTreeStructureChanged?.(...args),
        canRenameNode: (...args)=>resolve().canRenameNode?.(...args) ?? true,
        onRenamePersist: (...args)=>resolve().onRenamePersist?.(...args) ?? Promise.resolve(args[1]),
        onRenameStart: (...args)=>resolve().onRenameStart?.(...args),
        onRenameSuccess: (...args)=>resolve().onRenameSuccess?.(...args),
        onRenameError: (...args)=>resolve().onRenameError?.(...args),
        prepareCreatableDraft: (...args)=>resolve().prepareCreatableDraft?.(...args),
        getCreatableSelectedId: ()=>resolve().getCreatableSelectedId?.(),
        getCreatableRootPlacementIds: ()=>resolve().getCreatableRootPlacementIds?.() ?? [],
        onCreatablePersist: (...args)=>resolve().onCreatablePersist?.(...args) ?? Promise.resolve(),
        onCreatableDismiss: (...args)=>resolve().onCreatableDismiss?.(...args),
        onCreatableStart: (...args)=>resolve().onCreatableStart?.(...args),
        onCreatableSuccess: (...args)=>resolve().onCreatableSuccess?.(...args),
        onCreatableError: (...args)=>resolve().onCreatableError?.(...args)
    };
}
function createFolderTreeContextRefActions(getActions) {
    const actions = createFolderTreeRefActions(getActions);
    return {
        renderItem: (_ctx, createTreeNode, TreeNodeComponent, node)=>actions.renderItem?.(createTreeNode, TreeNodeComponent, node),
        renderIcon: (_ctx, icon, node)=>actions.renderIcon?.(icon, node),
        onAddClose: (_ctx, save, input, props)=>actions.onAddClose?.(save, input, props),
        onSelect: (_ctx, id)=>actions.onSelect?.(id),
        onCloseMultiToolbar: (_ctx, checked)=>actions.onCloseMultiToolbar?.(checked),
        onSearchResult: (_ctx, result)=>actions.onSearchResult?.(result),
        onNodeExpand: (_ctx, expanded, node)=>actions.onNodeExpand?.(expanded, node),
        onCheck: (_ctx, nodeId)=>actions.onCheck?.(nodeId),
        onSort: (_ctx, args)=>actions.onSort?.(args),
        onSortPersistStart: (_ctx)=>actions.onSortPersistStart?.(),
        onSortPersistSuccess: (_ctx)=>actions.onSortPersistSuccess?.(),
        onSortPersistError: (_ctx, error)=>actions.onSortPersistError?.(error),
        onSortStart: (_ctx, evt)=>actions.onSortStart?.(evt),
        onSortEnd: (_ctx, evt)=>actions.onSortEnd?.(evt),
        onSortMove: (_ctx, evt, originalEvent)=>actions.onSortMove?.(evt, originalEvent),
        onResize: (_ctx, width, collapse)=>actions.onResize?.(width, collapse),
        onResizeFinished: (_ctx, width)=>actions.onResizeFinished?.(width),
        onResizeOpposite: (_ctx, containerId, oppositeId, width, injectStyleFn)=>actions.onResizeOpposite?.(containerId, oppositeId, width, injectStyleFn),
        onTreeStructureChanged: (_ctx)=>actions.onTreeStructureChanged?.(),
        canRenameNode: (_ctx, node)=>actions.canRenameNode?.(node),
        onRenamePersist: (_ctx, id, inputValue, previousTitle)=>actions.onRenamePersist?.(id, inputValue, previousTitle),
        onRenameStart: (_ctx, inputValue)=>actions.onRenameStart?.(inputValue),
        onRenameSuccess: (_ctx, displayName)=>actions.onRenameSuccess?.(displayName),
        onRenameError: (_ctx, error)=>actions.onRenameError?.(error),
        prepareCreatableDraft: (_ctx, type)=>actions.prepareCreatableDraft?.(type),
        getCreatableSelectedId: (_ctx)=>actions.getCreatableSelectedId?.(),
        getCreatableRootPlacementIds: (_ctx)=>actions.getCreatableRootPlacementIds?.(),
        onCreatablePersist: (_ctx, inputValue, node, meta)=>actions.onCreatablePersist?.(inputValue, node, meta),
        onCreatableDismiss: (_ctx)=>actions.onCreatableDismiss?.(),
        onCreatableStart: (_ctx, inputValue)=>actions.onCreatableStart?.(inputValue),
        onCreatableSuccess: (_ctx, displayName)=>actions.onCreatableSuccess?.(displayName),
        onCreatableError: (_ctx, error)=>actions.onCreatableError?.(error)
    };
}
export { createFolderTreeContextRefActions, createFolderTreeRefActions };
